import React, { useState } from 'react'
import Datalist from 'components/Datalist'
import {
  useDefaultPrices,
  useProducts,
  GetDefaultPrice_getDefaultPrice_defaultPrices,
  useDeleteDefaultPrice,
  useTestLocations,
} from '@modmd/data'
import { useSortChangeHandler } from 'utils/helpers'
import { useSearchState } from 'internal-portal/utils/useSearchState'
import Button from 'components/Button'
import Icon from 'components/Icon'
import { DefaultPriceDatalist } from 'sharedComponents/DefaultPriceDatalist'
import { SelectInput } from 'components/SelectInput'
import { Box } from 'components/Layout'
import { ConfirmationDialog, Dialog } from 'components/Dialog'
import { Text } from 'components/Typography'
import { useToaster } from 'utils/useToaster'
import Card from 'components/Card'
import { CreateEditDefaultPrices } from '../components/CreateEditDefaultPrices'
import { getProducts, getLocations } from './utils'

type FilterState = {
  productId?: string
  locationId?: string
  pricesPage?: string
}

const PAGE_LENGTH = 10

const DefaultPrices = () => {
  const searchState = useSearchState<FilterState>()
  const { setToastMessage } = useToaster()
  const { sorter, handleSortChange } = useSortChangeHandler()
  const { products } = useProducts()
  const [confirmDeletePrice, setConfirmDeletePrice] = useState(false)
  const [isAddingDialog, setIsAddingDialog] = useState(false)
  const [defaultPriceItem, setDefaultPriceItem] = useState<
    GetDefaultPrice_getDefaultPrice_defaultPrices | undefined
  >(undefined)

  const productOptions = React.useMemo(() => (products ? getProducts(products) : []), [products])

  const { data: testLocations } = useTestLocations({
    showDeleted: false,
  })

  const testLocationsOptions = React.useMemo(
    () => (testLocations?.testLocations ? getLocations(testLocations) : []),
    [testLocations]
  )

  const { data: defaultPrices, loading, refetch } = useDefaultPrices({
    filter: {
      productId: searchState.data.productId,
      locationId: searchState.data.locationId,
    },
    page: Number(searchState.data.pricesPage) || 1,
    pageLength: PAGE_LENGTH,
  })

  const [deletePrice, { loading: isDeleteLoading }] = useDeleteDefaultPrice({
    onCompleted: async () => {
      setToastMessage('Data successfully deleted', 'success')
      await refetch()
    },
  })

  const onDelete = async () => {
    await deletePrice({
      variables: {
        id: String(defaultPriceItem?.id),
      },
    })
    setConfirmDeletePrice(false)
    setDefaultPriceItem(undefined)
  }

  const handleDelete = (
    item: GetDefaultPrice_getDefaultPrice_defaultPrices,
    e: React.MouseEvent<HTMLElement, MouseEvent>
  ) => {
    e.stopPropagation()
    setConfirmDeletePrice(true)
    setDefaultPriceItem(item)
  }

  const handleEdit = (
    item: GetDefaultPrice_getDefaultPrice_defaultPrices,
    e: React.MouseEvent<HTMLElement, MouseEvent>
  ) => {
    e.stopPropagation()
    setIsAddingDialog(true)
    setDefaultPriceItem(item)
  }

  const onSubmit = async () => {
    setIsAddingDialog(false)
    setDefaultPriceItem(undefined)
    await refetch()
  }

  return (
    <>
      <DefaultPriceDatalist
        prices={defaultPrices?.getDefaultPrice?.defaultPrices || []}
        pagination={defaultPrices?.getDefaultPrice?.pagination}
        isLoading={loading}
        tableHeader={
          <Datalist.Title
            rightContent={
              <>
                <Box
                  display="grid"
                  gridGap="1rem"
                  gridTemplateColumns={{
                    TABLET: 'repeat(2, 1fr)',
                    DESKTOP: '0.7fr 0.7fr',
                  }}
                >
                  <SelectInput
                    name="productId"
                    label="Product"
                    onChange={(e) => {
                      searchState.setSearchState(
                        { productId: e.target.value, pricesPage: 1 },
                        false
                      )
                    }}
                    value={searchState.data.productId ?? ''}
                    options={[{ label: 'All products', value: '' }, ...productOptions]}
                  />
                  <SelectInput
                    name="locationId"
                    label="Location"
                    onChange={(e) => {
                      searchState.setSearchState(
                        { locationId: e.target.value, pricesPage: 1 },
                        false
                      )
                    }}
                    value={searchState.data.locationId ?? ''}
                    options={[{ label: 'All locations', value: '' }, ...testLocationsOptions]}
                  />
                </Box>
                <Button
                  colorVariant="primary"
                  onClick={() => {
                    setIsAddingDialog(true)
                  }}
                >
                  <Icon.Plus size="L" /> Add Price
                </Button>
              </>
            }
          >
            Default Product Prices
          </Datalist.Title>
        }
        sorter={sorter}
        onSortChange={handleSortChange}
        requestDebounceInMs={250}
        onPageChange={(pageNumber) => {
          searchState.setSearchState({ pricesPage: pageNumber }, false)
        }}
        pageLength={defaultPrices?.getDefaultPrice?.pagination.length}
        handleDelete={handleDelete}
        handleEdit={handleEdit}
      />
      <Dialog
        isOpen={!!isAddingDialog}
        onDismiss={() => {
          setIsAddingDialog(false)
          setDefaultPriceItem(undefined)
        }}
        variant="center"
        maxWidth="545px"
      >
        <Card.Title
          onDismiss={() => {
            setIsAddingDialog(false)
            setDefaultPriceItem(undefined)
          }}
        >
          Add Prices
        </Card.Title>
        <Card.Content>
          <CreateEditDefaultPrices defaultPriceItem={defaultPriceItem} onSubmit={onSubmit} />
        </Card.Content>
      </Dialog>
      <ConfirmationDialog
        title="Delete default price"
        isOpen={confirmDeletePrice}
        onDismiss={() => {
          setConfirmDeletePrice(false)
          setDefaultPriceItem(undefined)
        }}
        actions={
          <>
            <Button
              onClick={() => {
                setConfirmDeletePrice(false)
                setDefaultPriceItem(undefined)
              }}
              colorVariant="secondary"
            >
              Cancel
            </Button>
            <Button onClick={onDelete} isFetching={isDeleteLoading} disabled={isDeleteLoading}>
              Delete
            </Button>
          </>
        }
      >
        <Box display="flex" flexDirection="column">
          <Text>
            Are you sure you want to remove {defaultPriceItem?.Product?.name}, from{' '}
            {defaultPriceItem?.TestLocation?.name}, with the price of ${defaultPriceItem?.price}?
          </Text>
        </Box>
      </ConfirmationDialog>
    </>
  )
}

export { DefaultPrices }
